import { Block, TimePlan } from "@/store/time-of-use/types"
import { Country } from "@/types/country"

export type PricingPlansState = {
    configList: PriceConfig[]
    countryDefaultConfigList: CountryDefaultConfig[]
    loading: Record<LoadingTypes, boolean>
    timeplanConfigList: TimeplanPriceConfig[]
    priceLists: PriceList[]
    priceList: PriceList | null
    editConfig: PriceConfig | null
    adjustmentBreakdown: AdjustmentBreakdownDto | null
}

export enum PricingPlanType {
    PRICING_SUPPLIER_ENERGY_BUY = "SUPPLIER_ENERGY_BUY",
    PRICING_SUPPLIER_ENERGY_SELL = "SUPPLIER_ENERGY_SELL",
    PRICING_DISTRIBUTOR_ENERGY = "DISTRIBUTOR_ENERGY",
    PRICING_DISTRIBUTOR_POWER = "DISTRIBUTOR_POWER",
}

export const PricingPlanTypes = {
    [PricingPlanType.PRICING_SUPPLIER_ENERGY_BUY]: "Supplier Energy Buy",
    [PricingPlanType.PRICING_SUPPLIER_ENERGY_SELL]: "Supplier Energy Sell",
    [PricingPlanType.PRICING_DISTRIBUTOR_ENERGY]: "Distributor Energy",
    [PricingPlanType.PRICING_DISTRIBUTOR_POWER]: "Distributor Power",
} as const

export type PriceList = {
    id: number
    companyId: number | null
    ownerId: number | null
    countryId: number | null
    name: string
    validFrom: string | null
    validTo: string | null

    distributorEnergyType: string | null //change type for all those types "FIXED" | "SPOT" | "TIME_OF_USE"
    distributorEnergyPlanId: number | null
    distributorEnergyPrice: number | null
    distributorEnergyPriceTypeConfig: number | null

    distributorPowerType: string | null
    distributorPowerPlanId: number | null
    distributorPowerPrice: number | null
    distributorPowerPriceTypeConfig: number | null

    supplierEnergyBuyType: string | null
    supplierEnergyBuyPlanId: number | null
    supplierEnergyBuyPrice: number | null
    supplierEnergyBuyFee: number | null
    supplierEnergyBuyFeeType: string | null
    supplierEnergyBuyPriceTypeConfig: number | null

    supplierEnergySellType: string | null
    supplierEnergySellPlanId: number | null
    supplierEnergySellPrice: number | null
    supplierEnergySellFee: number | null
    supplierEnergySellFeeType: string | null
    supplierEnergySellPlanDefinitionId: number | null

    currencyCode: string
    createdAt: string
    updatedAt: string
    deletedAt: string | null
}

export type SpotFeeType = "FIXED" | "PERCENTAGE"

export type PriceAveragingWindow = "NONE" | "HALF_HOUR" | "HOUR"


export type SpotAdjustment = {
    id?: number
    name: string | null
    adjustmentType: "FEE" | "SUBSIDY" | "CLAMP"
    adjustmentAmountType: SpotFeeType
    amount: number | null
    clampMin: number | null
    clampMax: number | null
    currencyCode: string
    unit: string
    position: number
}

export type BasePriceConfig = {
    id: number | null
    name: string | null
    currencyCode: string | null
    planType: ConfigSourceType | null
    priceType: PricingPlanType
    fee: number | null
    feeType: SpotFeeType | null
    priceAveragingWindow?: PriceAveragingWindow
}

export type PostEntries = {
    timePlanBlockId: number
    price: number
}[]

export type GetEntries = {
    id: number
    price: {
        amount: number
        currency: string
    }
    timePlanBlock: Block
}[]

export type GetPrice = {
    price: {
        amount: number
        currency: string
    } | null
}

export type PostPrice = {
    price: number | null
}

export type GetPriceConfig = BasePriceConfig & {
    entries: GetEntries
    timePlan: TimePlan | null
    isCustom: boolean
    adjustments: SpotAdjustment[]
} & GetPrice

export type PriceConfig = BasePriceConfig & {
    entries: PostEntries
    timePlanId: number | null
} & PostPrice

export type PricePreviewConfig = BasePriceConfig & {
    entries: GetEntries
    timePlanId: number | null
} & PostPrice

export type TimeplanPriceConfig = Omit<PriceConfig, "timeplanBlockId"> & {
    timePlanBlock: {
        id: number
        title: string
        color: string
    } | null
}

export const STORE_NAME = "pricingPlans"

export enum LoadingTypes {
    CONFIG_LIST = "CONFIG_LIST",
    COUNTRY_CONFIG_LIST = "COUNTRY_CONFIG_LIST",
    PRICE_LISTS = "PRICE_LISTS",
    TIMEPLAN_CONFIG_LIST = "TIMEPLAN_CONFIG_LIST",
    PRICE_LIST = "PRICE_LIST",
    CONFIG_BY_ID = "CONFIG_BY_ID",
    ADJUSTMENT_BREAKDOWN = "ADJUSTMENT_BREAKDOWN",
}

export enum PricingPlansMutations {
    SET_CONFIG_LIST = "setConfigList",
    SET_PRICE_LIST = "setPriceList",
    SET_LOADING = "setLoading",
    SET_TIMEPLAN_CONFIG_LIST = "setTimeplanConfigList",
    SET_EDIT_CONFIG = "setEditConfig",
    SET_ADJUSTMENT_BREAKDOWN = "setAdjustmentBreakdown",
}

export const PricingPlansActions = {
    FETCH_CONFIG_LIST: `${STORE_NAME}/fetchConfigList`,
    FETCH_DEFAULT_COUNTRY_CONFIG_LIST: `${STORE_NAME}/fetchDefaultCountryConfigList`,
    FETCH_CONFIG_BY_ID: `${STORE_NAME}/fetchConfigById`,
    FETCH_TIMEPLAN_CONFIG_LIST: `${STORE_NAME}/fetchTimeplanConfigList`,
    FETCH_PRICE_LISTS: `${STORE_NAME}/fetchPriceLists`,
    FETCH_PRICE_LIST: `${STORE_NAME}/fetchPriceListById`,
    POST_CONFIG: `${STORE_NAME}/postConfig`,
    PUT_OR_POST_CONFIG: `${STORE_NAME}/putOrPostConfig`,
    POST_PRICE_LIST: `${STORE_NAME}/postPriceList`,
    PUT_CONFIG: `${STORE_NAME}/putConfig`,
    PUT_PRICE_LIST: `${STORE_NAME}/putPriceList`,
    DELETE_CONFIG: `${STORE_NAME}/deleteConfig`,
    DELETE_PRICE_LIST: `${STORE_NAME}/deletePriceList`,
    FETCH_ADJUSTMENT_BREAKDOWN: `${STORE_NAME}/fetchAdjustmentBreakdown`,
} as const

export const ApiRoutes = {
    CONFIGS: "/price-type-configs",
    CONFIGS_BATCH: "/pricing/configs/batch",
    PRICE_LIST: "/pricing/price-lists",
    PRICE_LIST_CONFIGS: "/pricing/price-lists/configs",
    TIME_PLANS: "/timeplans",
    TP_CONFIGS: "/configs",
    ADJUSTMENTS_BREAKDOWN: "/pricing/adjustments/breakdown",
} as const

export const ConfigSourceTypes = {
    TIME_OF_USE: "TIME_OF_USE",
    FIXED: "FIXED",
    SPOT_MARKET: "SPOT_MARKET",
} as const

export const ConfigSourceTypeLabels: Record<ConfigSourceType, string> = {
    TIME_OF_USE: "Time of use",
    FIXED: "Fixed",
    SPOT_MARKET: "Spot market",
} as const

export type ConfigSourceType = keyof typeof ConfigSourceTypes

export type CountryDefaultConfig = {
    id: number
    country: Country
    config: PriceConfig
    priceType: PricingPlanType
}

export type MoneyDto = {
    amount: string
    currency: string
}

export type AdjustmentAppliedDto = {
    name: string | null
    adjustmentType: "FEE" | "SUBSIDY" | "CLAMP"
    adjustmentAmountType: SpotFeeType
    amount: string | null
    currencyCode: string
    unit: string | null
    clampMin: string | null
    clampMax: string | null
    delta: MoneyDto
    result: MoneyDto
}

export type AdjustmentBreakdownDto = {
    base: MoneyDto
    total: MoneyDto
    adjustments: AdjustmentAppliedDto[]
}
